import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { COLORS } from '../pages/IndividualPlan/tokens';
import {
  EMPTY_CELL,
  bandsOf,
  cellInk,
  cellText,
  levelOf,
  windowLabel,
  yearLabel,
  type GridDomainModel,
  type GridModel,
  type SchemeLevel,
} from './flashlightGrid';

const FONT = '"Nunito Sans", "Helvetica", "Arial", sans-serif';
const NAME_WIDTH = 200;
const CELL_WIDTH = 96;
const CELL_HEIGHT = 36;

const rowSx = { display: 'flex', alignItems: 'center', gap: '4px', fontFamily: FONT, fontSize: 16, lineHeight: '24px', fontWeight: 500, color: COLORS.ink } as const;
const cellSx = { width: CELL_WIDTH, height: CELL_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: COLORS.muted } as const;

function yearStart(index: number) {
  return index === 3 ? { borderLeft: '2px solid #FFFFFF' } : {};
}

type Bands = ReturnType<typeof bandsOf>;

function Row({ id, name, values, average, bands }: { id: string; name: string; values: (number | null)[]; average: boolean; bands: [Bands, Bands] }) {
  return (
    <Box data-testid={`grid-row-${id}`} sx={rowSx}>
      <Typography component="span" data-testid="grid-name" sx={{ width: NAME_WIDTH, flexShrink: 0, fontFamily: FONT, fontSize: 16, lineHeight: '24px', fontWeight: average ? 700 : 500 }}>
        {name}
      </Typography>
      {values.map((value, i) => {
        const yearBands = bands[i < 3 ? 0 : 1];
        const level = levelOf(yearBands, value);
        const band = level > 0 ? yearBands[level - 1] : null;
        return (
          <Box
            key={i}
            data-testid="grid-cell"
            sx={{ ...cellSx, ...yearStart(i), fontWeight: average ? 700 : 600, bgcolor: band ? band.color : 'transparent', color: band ? cellInk(band.color) : COLORS.muted }}
          >
            {cellText(value, average)}
          </Box>
        );
      })}
    </Box>
  );
}

function Legend({ bands, prefix }: { bands: Bands; prefix: string }) {
  return (
    <Box data-testid="grid-legend" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px', fontFamily: FONT, fontSize: 16, lineHeight: '24px', color: COLORS.muted }}>
      {prefix && <Box component="span">{prefix}</Box>}
      {bands.map((band) => (
        <Box key={band.label} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <Box component="i" data-testid="legend-dot" sx={{ width: 12, height: 12, borderRadius: '9999px', display: 'inline-block', bgcolor: band.color }} />
          {`${band.score} ${band.label}`}
        </Box>
      ))}
    </Box>
  );
}

function schemeKey(scheme: SchemeLevel[]): string {
  return scheme.map((level) => `${level.score}:${level.label}:${level.color}`).join('|');
}

export function FlashlightBenchmarkGrid({ model, scheme, chipFor }: { model: GridModel; scheme: SchemeLevel[]; chipFor?: (domain: GridDomainModel) => ReactNode }) {
  const schemes: [SchemeLevel[], SchemeLevel[]] = [model.schemes[0] ?? scheme, model.schemes[1] ?? scheme];
  const bands: [Bands, Bands] = [bandsOf(schemes[0]), bandsOf(schemes[1])];
  const sameScheme = schemeKey(schemes[0]) === schemeKey(schemes[1]);
  return (
    <Box data-testid="benchmark-grid" sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Box sx={rowSx}>
          <Box sx={{ width: NAME_WIDTH, flexShrink: 0 }} />
          {model.years.map((year) => (
            <Typography key={year} component="span" data-testid="grid-year" sx={{ width: CELL_WIDTH * 3 + 8, flexShrink: 0, fontFamily: FONT, fontSize: 16, lineHeight: '24px', fontWeight: 700 }}>
              {yearLabel(year)}
            </Typography>
          ))}
        </Box>
        <Box sx={rowSx}>
          <Box sx={{ width: NAME_WIDTH, flexShrink: 0 }} />
          {model.columns.map((column, i) => (
            <Box key={`${column.schoolYear}-${column.window}`} data-testid="grid-window" sx={{ width: CELL_WIDTH, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', color: COLORS.muted, ...yearStart(i) }}>
              <Typography component="b" sx={{ fontFamily: FONT, fontSize: 16, lineHeight: '24px', fontWeight: 600 }}>{column.window.toUpperCase()}</Typography>
              {column.month && <Typography component="span" sx={{ fontFamily: FONT, fontSize: 16, lineHeight: '24px' }}>{windowLabel(column.month)}</Typography>}
            </Box>
          ))}
        </Box>
        {model.domains.map((domain) => (
          <Box key={domain.domain} sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', pt: '8px' }}>
              <Typography component="span" data-testid={`grid-domain-${domain.domain}`} sx={{ fontFamily: FONT, fontSize: 16, lineHeight: '24px', fontWeight: 600, color: COLORS.muted }}>
                {domain.label}
              </Typography>
              {chipFor?.(domain)}
              {domain.months.map((month, i) => month && (
                <Typography key={`${model.columns[i]!.schoolYear}-${model.columns[i]!.window}`} component="span" data-testid={`grid-domain-month-${domain.domain}`} sx={{ fontFamily: FONT, fontSize: 16, lineHeight: '24px', color: COLORS.muted }}>
                  {`${model.columns[i]!.window.toUpperCase()} ${windowLabel(month)}`}
                </Typography>
              ))}
            </Box>
            <Row id={`${domain.domain}-average`} name="Domain average" values={domain.average} average bands={bands} />
            {domain.indicators.map((indicator) => (
              <Row key={indicator.name} id={`${domain.domain}-${indicator.name}`} name={indicator.name} values={indicator.values} average={false} bands={bands} />
            ))}
          </Box>
        ))}
      </Box>
      {sameScheme ? (
        <Box data-testid="grid-legend" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px', fontFamily: FONT, fontSize: 16, lineHeight: '24px', color: COLORS.muted }}>
          {bands[1].map((band) => (
            <Box key={band.label} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <Box component="i" data-testid="legend-dot" sx={{ width: 12, height: 12, borderRadius: '9999px', display: 'inline-block', bgcolor: band.color }} />
              {`${band.score} ${band.label}`}
            </Box>
          ))}
          <Box component="span">{`${EMPTY_CELL} not scored yet`}</Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Legend bands={bands[0]} prefix={`${yearLabel(model.years[0])}:`} />
          <Box data-testid="grid-legend" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px', fontFamily: FONT, fontSize: 16, lineHeight: '24px', color: COLORS.muted }}>
            <Box component="span">{`${yearLabel(model.years[1])}:`}</Box>
            {bands[1].map((band) => (
              <Box key={band.label} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <Box component="i" data-testid="legend-dot" sx={{ width: 12, height: 12, borderRadius: '9999px', display: 'inline-block', bgcolor: band.color }} />
                {`${band.score} ${band.label}`}
              </Box>
            ))}
            <Box component="span">{`${EMPTY_CELL} not scored yet`}</Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
